import React, { memo, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '@iq/react-components';

import { getHasPermission } from '../../../../../bundles/auth';
import { getTimezone } from '../../../../../bundles/application';
import { updateAlarm } from '../../../../../bundles/alarms';
import { formatSiteDatetime } from '../../../../../datetimeUtils';
import LabelWithTooltip from '../../../../LabelWithTooltip';
import ConfirmationDialog from '../../../../ConfirmationDialog';
import { RelatedEvents } from '../../../EventTimelinePanel';
import { useActiveListEvents } from '../../../EventTimelinePanel/EventTimelineState';
import AlarmTable from './AlarmTable';
import { getEventTypeColor, getEventTypeLabel } from '../../utils';

import './UserEventsTab.scss';

const UserEventsTab = memo(
  ({ alarm, site, relatedEventsValue, hasLinkedEvents, alarmQueryParameters }) => {
    const dispatch = useDispatch();
    const timezone = useSelector(getTimezone);
    const [showModal, setShowModal] = useState(false);
    const [confirmRemoveEvents, setConfirmRemoveEvents] = useState(false);
    const canWriteAlarms = useSelector((state) =>
      getHasPermission(state, 'alarms/Write', { org: site?.org, site: site?.id })
    );

    const { events: linkedEvents } = useActiveListEvents(undefined, false);
    // Nothing saved yet, so ignore whatever the (fallback-range) query happens to return.
    // Ranged events are split into start/end timeline entries; keep one row per event.
    const events = useMemo(
      () => (hasLinkedEvents ? linkedEvents.filter((item) => item.type !== 'user-end') : []),
      [linkedEvents, hasLinkedEvents]
    );

    const columns = useMemo(
      () => [
        {
          Header: 'Date',
          accessor: 'from',
          Cell: ({ row }) => formatSiteDatetime(row.original.event.from, timezone),
        },
        {
          Header: 'Type',
          accessor: 'type',
          Cell: ({ row }) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: getEventTypeColor(row.original.event.type),
                  flexShrink: 0,
                }}
              />
              <span style={{ textTransform: 'capitalize' }}>
                {getEventTypeLabel(row.original.event.type)}
              </span>
            </div>
          ),
        },
        {
          Header: 'Event name',
          accessor: 'name',
          Cell: ({ row }) => {
            const name = row.original.event.name || '-';
            return <LabelWithTooltip name={name} />;
          },
        },
      ],
      [timezone]
    );

    const handleSaveFilters = useCallback(
      (newFilters) => {
        dispatch(
          updateAlarm(
            alarm.id,
            { relatedEvents: newFilters },
            { queryParameters: alarmQueryParameters }
          )
        );
      },
      [dispatch, alarm.id, alarmQueryParameters]
    );

    const handleAddDone = useCallback(() => {
      setShowModal(false);
    }, []);

    const handleRemoveRelatedEvents = () => {
      dispatch(
        updateAlarm(alarm.id, { relatedEvents: {} }, { queryParameters: alarmQueryParameters })
      );
      setConfirmRemoveEvents(false);
    };

    return (
      <div className="user-events-tab">
        <div className="user-events-header">
          <h3 className="user-events-title">
            Linked user events (e.g. maintenance or inspection) that help fix the alarm
          </h3>
          {canWriteAlarms && (
            <div className="user-events-header-buttons">
              {events.length > 0 && (
                <Button
                  activity="danger"
                  onClick={() => setConfirmRemoveEvents(true)}
                >
                  Unlink user events
                </Button>
              )}
              <Button
                activity="secondary"
                onClick={() => setShowModal(true)}
              >
                Link user events
              </Button>
            </div>
          )}
        </div>

        <div className="user-events-content">
          {events.length === 0 ? (
            <div className="user-events-empty-results">
              <p>No Linked user events</p>
            </div>
          ) : (
            <AlarmTable
              columns={columns}
              data={events}
              classPrefix="user-events"
            />
          )}
        </div>

        {showModal && (
          <RelatedEvents
            site={site}
            value={relatedEventsValue}
            addEvents
            title="Link user events"
            onChange={handleSaveFilters}
            onAddDone={handleAddDone}
            enabledEventTypes={['user']}
          />
        )}

        {confirmRemoveEvents && (
          <ConfirmationDialog
            onCancel={() => setConfirmRemoveEvents(false)}
            onConfirm={handleRemoveRelatedEvents}
            confirmType="danger"
            showCancel
            title="Unlink user events"
            body={
              <div className="remove-event-confirm">
                <div>Are you sure you want to unlink user events?</div>
                <div>
                  This will not delete user events, but rather clear any user event relations
                  defined via the link user events filters.
                </div>
              </div>
            }
            confirmText="Remove"
          />
        )}
      </div>
    );
  }
);

UserEventsTab.displayName = 'UserEventsTab';

export default UserEventsTab;
